/**
 * Robust geolocation helper, tuned for iPad/Safari PWA use.
 *
 * Notes / pitfalls handled:
 *  - navigator.geolocation only works over HTTPS (or localhost). We surface a clear
 *    error instead of hanging.
 *  - iOS Safari can hang on getCurrentPosition; we always race it against our own timeout.
 *  - Permission denial / timeout never throws to the caller — the form must not block.
 */

export function isGeolocationSupported() {
    return typeof navigator !== 'undefined' && 'geolocation' in navigator && (window.isSecureContext || window.location.hostname === 'localhost');
}

/**
 * Get the current position. Resolves to { ok, lat, lng, accuracy } or { ok:false, error }.
 * Never rejects.
 */
export function getCurrentLocation({ timeout = 12000, enableHighAccuracy = true, maximumAge = 60000 } = {}) {
    return new Promise((resolve) => {
        if (!isGeolocationSupported()) {
            resolve({ ok: false, error: 'Geolocation is not available (requires HTTPS).' });
            return;
        }

        let settled = false;
        const finish = (result) => {
            if (settled) return;
            settled = true;
            resolve(result);
        };

        // Hard timeout in case the native call never calls back (seen on iOS).
        const timer = setTimeout(() => {
            finish({ ok: false, error: 'Location request timed out. You can enter coordinates manually.' });
        }, timeout + 1000);

        try {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    clearTimeout(timer);
                    finish({
                        ok: true,
                        lat: Number(pos.coords.latitude.toFixed(6)),
                        lng: Number(pos.coords.longitude.toFixed(6)),
                        accuracy: pos.coords.accuracy != null ? Math.round(pos.coords.accuracy) : null,
                    });
                },
                (err) => {
                    clearTimeout(timer);
                    const map = {
                        1: 'Location permission denied. You can enter coordinates manually.',
                        2: 'Location unavailable. You can enter coordinates manually.',
                        3: 'Location request timed out. You can enter coordinates manually.',
                    };
                    finish({ ok: false, error: map[err && err.code] || 'Could not get location.' });
                },
                { enableHighAccuracy, timeout, maximumAge }
            );
        } catch (e) {
            clearTimeout(timer);
            finish({ ok: false, error: 'Could not get location.' });
        }
    });
}

/** Human-readable "lat, lng (±Nm)" or empty string. */
export function formatGps(gps) {
    if (!gps || gps.lat == null || gps.lng == null || gps.lat === '' || gps.lng === '') return '';
    const acc = gps.accuracy ? ` (±${gps.accuracy}m)` : '';
    return `${gps.lat}, ${gps.lng}${acc}`;
}

/**
 * Reverse-geocode coordinates into a street address.
 * Uses the free, key-less OpenStreetMap Nominatim service (CORS-enabled, works on iPad Safari).
 * Resolves to { ok, address } or { ok:false, error }. Never rejects.
 * NOTE: For high-volume/production use, swap to a keyed provider (Google/Mapbox) — Nominatim
 * is rate-limited (~1 req/sec) and intended for light usage.
 */
export async function reverseGeocode(lat, lng, { timeout = 12000 } = {}) {
    if (lat == null || lng == null || lat === '' || lng === '') {
        return { ok: false, error: 'No coordinates to look up.' };
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
        const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&zoom=18&addressdetails=1`;
        const resp = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        clearTimeout(timer);
        if (!resp.ok) return { ok: false, error: `Address lookup failed (${resp.status}).` };
        const data = await resp.json();
        const address = (data && data.display_name) ? String(data.display_name) : '';
        return address ? { ok: true, address } : { ok: false, error: 'No address found for this location.' };
    } catch (e) {
        clearTimeout(timer);
        return { ok: false, error: e && e.name === 'AbortError' ? 'Address lookup timed out.' : 'Address lookup unavailable.' };
    }
}
